// C3 — Importar programación semanal de supervisores desde Excel.
//
// Plantilla plana: Fecha · Turno (número) · Supervisor (email).
// 1 supervisor por (fecha, turno) — las celdas ya ocupadas se marcan como error
// (import aditivo, no sobreescribe). El mismo supervisor en 2 turnos del mismo
// día es válido (doble turno), así que NO se valida ese caso.
//
// Cada fila válida se crea vía el endpoint single existente
// (POST /programacion-supervisores), que revalida del lado servidor.

import { useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import type { CoordinadorProgramacionSupervisoresSemana } from 'schemas';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { useCrearProgramacionSupervisor } from '../../lib/api/queries';
import { ApiError } from '../../lib/api/client';
import { parseFechaISO } from './excelFecha';

type Data = CoordinadorProgramacionSupervisoresSemana;

interface FilaParseada {
  idx: number;
  filaExcel: number;
  fecha: string;
  turnoTexto: string;
  supervisorTexto: string;
  turno_id: string | null;
  supervisor_user_id: string | null;
  error: string | null;
}

const norm = (s: string) => s.trim().toLowerCase();

export function C3ImportarSupervisoresDialog({
  data,
  onClose,
}: {
  data: Data;
  onClose: () => void;
}) {
  const [filas, setFilas] = useState<FilaParseada[]>([]);
  const [nombreArchivo, setNombreArchivo] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const crear = useCrearProgramacionSupervisor();
  const [cargando, setCargando] = useState(false);

  const turnoPorNumero = useMemo(() => {
    const m = new Map<number, string>();
    for (const t of data.turnos) m.set(t.numero, t.id);
    return m;
  }, [data.turnos]);
  const supervisorPorEmail = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of data.supervisores) m.set(norm(s.email), s.id);
    return m;
  }, [data.supervisores]);
  const supervisorLabel = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of data.supervisores) m.set(s.id, `${s.nombre} ${s.apellido}`);
    return m;
  }, [data.supervisores]);

  // Celdas (fecha|turno) ya ocupadas (1 supervisor por turno).
  const ocupadas = useMemo(() => {
    const s = new Set<string>();
    for (const p of data.programaciones) s.add(`${p.fecha}|${p.turno_id}`);
    return s;
  }, [data.programaciones]);

  function descargarPlantilla() {
    const wb = XLSX.utils.book_new();
    const header = ['Fecha (YYYY-MM-DD)', 'Turno (1/2/3)', 'Supervisor (email)'];
    const ejemplo = [data.dias[0], data.turnos[0]?.numero ?? 1, data.supervisores[0]?.email ?? ''];
    const ws = XLSX.utils.aoa_to_sheet([header, ejemplo]);
    XLSX.utils.book_append_sheet(wb, ws, 'Supervisores');

    const ref = [
      ['TURNOS', ''],
      ['Número', 'Horario'],
      ...[...data.turnos]
        .sort((a, b) => a.numero - b.numero)
        .map((t) => [t.numero, `${t.hora_inicio.slice(0, 5)}–${t.hora_fin.slice(0, 5)}`]),
      ['', ''],
      ['SUPERVISORES', ''],
      ['Nombre', 'Email'],
      ...data.supervisores.map((s) => [`${s.nombre} ${s.apellido}`, s.email]),
    ];
    const wsRef = XLSX.utils.aoa_to_sheet(ref);
    XLSX.utils.book_append_sheet(wb, wsRef, 'Referencia');
    XLSX.writeFile(wb, `programacion-supervisores-${data.semana_iso}.xlsx`);
  }

  function onArchivo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setNombreArchivo(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target?.result, { type: 'array' });
        const ws = wb.Sheets['Supervisores'] ?? wb.Sheets[wb.SheetNames[0]!];
        const aoa = XLSX.utils.sheet_to_json<unknown[]>(ws!, { header: 1, raw: false, defval: '' });
        // Segunda pasada cruda para tomar el serial de la fecha (ver excelFecha).
        const aoaRaw = XLSX.utils.sheet_to_json<unknown[]>(ws!, {
          header: 1,
          raw: true,
          defval: '',
        });
        const diasSet = new Set(data.dias);
        const vistasCelda = new Set<string>();
        const out: FilaParseada[] = [];
        for (let i = 1; i < aoa.length; i++) {
          const r = aoa[i]!;
          const fecha = parseFechaISO(aoaRaw[i]?.[0] ?? r[0]);
          const turnoTexto = String(r[1] ?? '').trim();
          const supervisorTexto = String(r[2] ?? '').trim();
          if (!fecha && !turnoTexto && !supervisorTexto) continue;

          const turnoNum = Number(turnoTexto.replace(/[^0-9]/g, ''));
          const turno_id = Number.isFinite(turnoNum)
            ? (turnoPorNumero.get(turnoNum) ?? null)
            : null;
          const supervisor_user_id = supervisorPorEmail.get(norm(supervisorTexto)) ?? null;

          let error: string | null = null;
          if (!diasSet.has(fecha)) error = `Fecha "${fecha}" fuera de la semana`;
          else if (!turno_id) error = `Turno "${turnoTexto}" no válido`;
          else if (!supervisor_user_id) error = `Supervisor "${supervisorTexto}" no encontrado`;
          else {
            const celdaKey = `${fecha}|${turno_id}`;
            if (ocupadas.has(celdaKey)) error = 'Turno ya tiene supervisor (quítelo primero)';
            else if (vistasCelda.has(celdaKey)) error = 'Fila duplicada en el archivo';
            else vistasCelda.add(celdaKey);
          }

          out.push({
            idx: out.length,
            filaExcel: i + 1,
            fecha,
            turnoTexto,
            supervisorTexto,
            turno_id,
            supervisor_user_id,
            error,
          });
        }
        setFilas(out);
        if (out.length === 0) toast.error('El archivo no tiene filas de datos');
      } catch {
        toast.error('No se pudo leer el archivo. ¿Es un .xlsx válido?');
      }
    };
    reader.readAsArrayBuffer(file);
  }

  const cargables = filas.filter((f) => !f.error);
  const errores = filas.filter((f) => f.error);
  const puedeCargar = cargables.length > 0 && !cargando;

  async function cargar() {
    if (!puedeCargar) return;
    setCargando(true);
    let ok = 0;
    const fails: string[] = [];
    for (const f of cargables) {
      try {
        await crear.mutateAsync({
          fecha: f.fecha,
          turno_id: f.turno_id!,
          supervisor_user_id: f.supervisor_user_id!,
        });
        ok++;
      } catch (err) {
        fails.push(`Fila ${f.filaExcel}: ${err instanceof ApiError ? err.message : 'error'}`);
      }
    }
    setCargando(false);
    if (ok > 0) toast.success(`${ok} asignación(es) de supervisor cargadas`);
    if (fails.length > 0) {
      toast.error(`${fails.length} fila(s) no se cargaron`, {
        description: fails.slice(0, 3).join(' · '),
      });
    } else {
      onClose();
    }
  }

  return (
    <Dialog open onClose={onClose} className="max-w-3xl">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Importar supervisores desde Excel · {data.semana_iso}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="outline" type="button" onClick={descargarPlantilla}>
              Descargar plantilla
            </Button>
            <Button variant="outline" type="button" onClick={() => inputRef.current?.click()}>
              Subir archivo
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={onArchivo}
            />
            {nombreArchivo ? (
              <span className="text-xs text-fg-tertiary">{nombreArchivo}</span>
            ) : null}
          </div>

          {filas.length > 0 ? (
            <div className="flex flex-col gap-2">
              <p className="text-sm">
                {cargables.length} lista(s)
                {errores.length > 0 ? (
                  <span className="text-crit"> · {errores.length} con error</span>
                ) : null}
              </p>
              <div className="max-h-80 overflow-auto rounded border border-border">
                <table className="w-full text-xs">
                  <thead className="bg-surface-overlay text-fg-tertiary">
                    <tr>
                      <th className="px-2 py-1 text-left">Fila</th>
                      <th className="px-2 py-1 text-left">Fecha</th>
                      <th className="px-2 py-1 text-left">Turno</th>
                      <th className="px-2 py-1 text-left">Supervisor</th>
                      <th className="px-2 py-1 text-left">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filas.map((f) => (
                      <tr key={f.idx} className="border-t border-border">
                        <td className="px-2 py-1 align-top text-fg-tertiary">{f.filaExcel}</td>
                        <td className="px-2 py-1 align-top">{f.fecha}</td>
                        <td className="px-2 py-1 align-top">{f.turnoTexto}</td>
                        <td className="px-2 py-1 align-top">
                          {f.supervisor_user_id
                            ? supervisorLabel.get(f.supervisor_user_id)
                            : f.supervisorTexto}
                        </td>
                        <td className="px-2 py-1 align-top">
                          {f.error ? (
                            <span className="text-crit">{f.error}</span>
                          ) : (
                            <span className="text-ok">Lista</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="text-sm text-fg-tertiary">
              Descargue la plantilla (Fecha · Turno · Supervisor), complétela y súbala. El sistema
              deriva el turno por número y el supervisor por email. Cada turno admite un solo
              supervisor; las celdas ya ocupadas se marcan como error. El import es aditivo.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={cargar} disabled={!puedeCargar}>
            {cargando ? 'Cargando…' : `Cargar ${cargables.length} fila(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
